/**
 * PdfNodeView - メモに埋め込んだ PDF の表示コンポーネント。
 *
 * - src が無い: PDF を選んでアップロードする欄 (進捗 % を表示)
 * - src がある: ファイル名・サイズ・1 ページずつのプレビュー・ページ送り・新しいタブで開くリンク
 */

import { ChevronLeft, ChevronRight, ExternalLink, FileText } from 'lucide-react'
import { lazy, Suspense, useEffect, useEffectEvent, useRef, useState } from 'react'
import { toast } from 'sonner'

import { MAX_NOTE_PDF_SIZE, uploadNotePdf } from '@/api/services/memo'
import { isPdfFile, takePendingFile } from '@/components/tiptap-extension/file-drop-paste-extension'
import { Spinner } from '@/components/ui/spinner'
import { useSignedUrl } from '@/hooks/useSignedUrl'
import { apiErrorMessage } from '@/lib/api-error'
import { formatBytes } from '@/lib/format-utils'
import { cn } from '@/lib/utils'
import type { NodeViewProps } from '@tiptap/react'
import { NodeViewWrapper } from '@tiptap/react'

const PdfPreview = lazy(() => import('./pdf-preview'))

const DEFAULT_HEIGHT = 480
const MIN_HEIGHT = 160
const MAX_HEIGHT = 2000

const clampHeight = (h: number): number => Math.round(Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, h)))

type UploadedPdf = { src: string; name: string; size: number; pendingFileId: null }

export const PdfNodeView = ({ node, editor, selected, updateAttributes }: NodeViewProps) => {
  const src = (node.attrs.src ?? null) as string | null
  const name = String(node.attrs.name ?? '')
  const size = (node.attrs.size ?? null) as number | null
  const height = (node.attrs.height ?? null) as number | null
  const pendingFileId = (node.attrs.pendingFileId ?? null) as string | null

  return (
    <NodeViewWrapper className="my-2" data-drag-handle="">
      <div
        contentEditable={false}
        className={cn(
          'overflow-hidden rounded-lg border bg-card',
          // 画像ノードの選択表示と揃える
          selected && 'outline-2 outline-(--tt-brand-color-500)',
        )}
      >
        {src ? (
          <PdfEmbedCard
            src={src}
            name={name}
            size={size}
            height={height}
            canEdit={editor.isEditable}
            onHeightChange={(h) => updateAttributes({ height: h })}
          />
        ) : (
          <PdfUploadPlaceholder
            canEdit={editor.isEditable}
            pendingFileId={pendingFileId}
            onUploaded={updateAttributes}
          />
        )}
      </div>
    </NodeViewWrapper>
  )
}

function PdfUploadPlaceholder({
  canEdit,
  pendingFileId,
  onUploaded,
}: {
  canEdit: boolean
  pendingFileId: string | null
  onUploaded: (attrs: UploadedPdf) => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const abortRef = useRef<AbortController | null>(null)
  const [progress, setProgress] = useState<number | null>(null)

  useEffect(() => () => abortRef.current?.abort(), [])

  const handleFile = async (file: File) => {
    if (!isPdfFile(file)) {
      toast.error('PDF ファイルを選んでください')
      return
    }
    if (file.size > MAX_NOTE_PDF_SIZE) {
      toast.error(`PDF のサイズは ${MAX_NOTE_PDF_SIZE / (1024 * 1024)}MB までです`)
      return
    }

    const controller = new AbortController()
    abortRef.current = controller
    setProgress(0)
    try {
      const url = await uploadNotePdf(file, setProgress, controller.signal)
      onUploaded({ src: url, name: file.name, size: file.size, pendingFileId: null })
    } catch (error) {
      if (controller.signal.aborted) return
      toast.error(apiErrorMessage(error, 'PDF のアップロードに失敗しました'))
      setProgress(null)
    }
  }

  // ドロップ・貼り付けで挿入されたときは預かったファイルをそのままアップロードする
  const uploadPendingFile = useEffectEvent((id: string | null) => {
    const file = takePendingFile(id)
    if (file) void handleFile(file)
  })
  useEffect(() => uploadPendingFile(pendingFileId), [pendingFileId])

  const isUploading = progress !== null

  return (
    <>
      <button
        type="button"
        className="flex w-full items-center gap-3 px-4 py-5 text-left text-sm text-muted-foreground hover:bg-muted/50 disabled:cursor-default disabled:hover:bg-transparent"
        onClick={() => inputRef.current?.click()}
        disabled={!canEdit || isUploading}
      >
        {isUploading ? <Spinner size={18} /> : <FileText className="size-5 shrink-0" />}
        <span>
          {isUploading
            ? `アップロード中… ${progress}%`
            : canEdit
              ? 'タップして PDF を選択'
              : 'PDF が未選択です'}
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0]
          // 同じファイルを選び直しても change が発火するよう空にしておく
          event.target.value = ''
          if (file) void handleFile(file)
        }}
      />
    </>
  )
}

function PdfEmbedCard({
  src,
  name,
  size,
  height,
  canEdit,
  onHeightChange,
}: {
  src: string
  name: string
  size: number | null
  height: number | null
  canEdit: boolean
  onHeightChange: (height: number) => void
}) {
  const { url } = useSignedUrl(src.replace(/^\/api/, ''))
  const [numPages, setNumPages] = useState<number | null>(null)
  const [page, setPage] = useState(1)
  const [failed, setFailed] = useState(false)
  // ドラッグ中は手元だけで高さを変え、離したときに属性へ保存する
  const [draftHeight, setDraftHeight] = useState<number | null>(null)
  const dragRef = useRef<{ startY: number; startHeight: number } | null>(null)
  const frameHeight = draftHeight ?? height ?? DEFAULT_HEIGHT

  const handleResizeStart = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.setPointerCapture(event.pointerId)
    dragRef.current = { startY: event.clientY, startHeight: frameHeight }
  }

  const handleResizeMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag) return
    setDraftHeight(clampHeight(drag.startHeight + event.clientY - drag.startY))
  }

  const handleResizeEnd = () => {
    if (!dragRef.current) return
    dragRef.current = null
    if (draftHeight !== null) onHeightChange(draftHeight)
    setDraftHeight(null)
  }

  const loading = (
    <div className="flex h-full items-center justify-center">
      <Spinner size={20} />
    </div>
  )

  return (
    <>
      <div className="flex items-center gap-2 border-b px-3 py-2 text-sm">
        <FileText className="size-4 shrink-0 text-red-500" />
        <span className="min-w-0 flex-1 truncate" title={name}>
          {name || 'PDF'}
        </span>
        {size != null && <span className="shrink-0 text-xs text-muted-foreground">{formatBytes(size)}</span>}
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="新しいタブで開く"
            title="新しいタブで開く"
          >
            <ExternalLink className="size-4" />
          </a>
        )}
      </div>

      <div className="bg-muted/40" style={{ height: frameHeight }}>
        {failed && <p className="p-4 text-sm text-destructive">PDF を読み込めませんでした。</p>}
        {!failed && !url && loading}
        {!failed && url && (
          <Suspense fallback={loading}>
            <PdfPreview
              url={url}
              pageNumber={page}
              height={frameHeight}
              onLoad={setNumPages}
              onError={() => setFailed(true)}
            />
          </Suspense>
        )}
      </div>

      {numPages != null && numPages > 1 && (
        <div className="flex items-center justify-center gap-4 border-t py-1.5 text-sm">
          <button
            type="button"
            className="rounded p-1 hover:bg-muted disabled:opacity-40 disabled:hover:bg-transparent"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            aria-label="前のページ"
          >
            <ChevronLeft className="size-4" />
          </button>
          <span className="tabular-nums">
            {page} / {numPages}
          </span>
          <button
            type="button"
            className="rounded p-1 hover:bg-muted disabled:opacity-40 disabled:hover:bg-transparent"
            onClick={() => setPage((p) => Math.min(numPages, p + 1))}
            disabled={page >= numPages}
            aria-label="次のページ"
          >
            <ChevronRight className="size-4" />
          </button>
        </div>
      )}

      {canEdit && (
        <div
          role="separator"
          aria-orientation="horizontal"
          aria-label="PDF の高さを変更"
          title="ドラッグして高さを変更"
          className="group flex h-3 cursor-ns-resize touch-none items-center justify-center border-t"
          onPointerDown={handleResizeStart}
          onPointerMove={handleResizeMove}
          onPointerUp={handleResizeEnd}
          onPointerCancel={handleResizeEnd}
          // ノードのドラッグ移動が始まらないようにする
          onMouseDown={(event) => event.preventDefault()}
        >
          <div className="h-1 w-10 rounded-full bg-muted-foreground/30 group-hover:bg-muted-foreground/60" />
        </div>
      )}
    </>
  )
}
